import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { hrmApi } from '../../api';
import { PageHeader, LoadingState, ErrorState, EmptyState } from '../../components/common';
import { getErrorMessage } from '../../api/client';
import { fmtCurrency, fmtMoney } from '../../utils/currency';

import { Modal } from '../../components/Modal';

type Tab = 'jobs' | 'pipeline' | 'offers';

const STAGES: Array<{ key: string; label: string; color: string }> = [
  { key: 'applied',   label: 'Applied',   color: 'bg-canvas-subtle text-ink-700 ring-ink-200' },
  { key: 'screening', label: 'Screening', color: 'bg-blue-50 text-blue-700 ring-blue-200' },
  { key: 'interview', label: 'Interview', color: 'bg-indigo-50 text-indigo-700 ring-indigo-200' },
  { key: 'offer',     label: 'Offer',     color: 'bg-purple-50 text-purple-700 ring-purple-200' },
  { key: 'hired',     label: 'Hired',     color: 'bg-sage-50 text-sage-700 ring-sage-200' },
  { key: 'rejected',  label: 'Rejected',  color: 'bg-red-50 text-red-700 ring-red-200' },
];

/** FR-HR-06 — Recruitment */
export function RecruitmentPage() {
  const [tab, setTab] = useState<Tab>('jobs');

  const TABS: Array<{ key: Tab; label: string }> = [
    { key: 'jobs',     label: 'Job postings' },
    { key: 'pipeline', label: 'Pipeline' },
    { key: 'offers',   label: 'Offers' },
  ];

  return (
    <div className="px-8 py-10 max-w-[96rem] mx-auto">
      <PageHeader
        eyebrow="People"
        title="Recruitment"
        subtitle="Open roles, candidate pipeline stages, and offer management."
      />

      <div className="card p-1.5 mb-6 inline-flex gap-1">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-1.5 text-sm font-semibold rounded-pill transition-all ${
              tab === t.key
                ? 'bg-canvas-subtle text-ink-900'
                : 'text-ink-600 hover:bg-canvas-subtle'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'jobs' && <JobsTab />}
      {tab === 'pipeline' && <PipelineTab />}
      {tab === 'offers' && <OffersTab />}
    </div>
  );
}

function JobsTab() {
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [closingJob, setClosingJob] = useState<any | null>(null);
  const q = useQuery({ queryKey: ['recruit-jobs'], queryFn: () => hrmApi.listJobs() });

  const transitionMut = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) => hrmApi.updateJob(id, { status }),
    onSuccess: () => {
      toast.success('Updated');
      qc.invalidateQueries({ queryKey: ['recruit-jobs'] });
      setClosingJob(null);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const deleteJobMut = useMutation({
    mutationFn: (id: string) => hrmApi.deleteJob(id),
    onSuccess: () => { toast.success('Job deleted'); qc.invalidateQueries({ queryKey: ['recruit-jobs'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const kpis = (() => {
    const rows = q.data ?? [];
    let open = 0, draft = 0, totalApps = 0, openHeadcount = 0;
    for (const j of rows) {
      if (j.status === 'open') { open++; openHeadcount += (j.headcount ?? 0) - (j.hired_count ?? 0); }
      if (j.status === 'draft') draft++;
      totalApps += j.application_count ?? 0;
    }
    return { total: rows.length, open, draft, totalApps, openHeadcount };
  })();

  return (
    <div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiTile label="Postings" value={kpis.total} />
        <KpiTile label="Open" value={kpis.open} accent={kpis.open > 0 ? 'sage' : 'default'} />
        <KpiTile label="Open headcount" value={kpis.openHeadcount}
          accent={kpis.openHeadcount > 0 ? 'amber' : 'default'} />
        <KpiTile label="Applications" value={kpis.totalApps} />
      </div>

      <div className="flex justify-end mb-4">
        <button className="btn-primary" onClick={() => setShowCreate(true)}>+ New job posting</button>
      </div>

      {q.isLoading ? <div className="card"><LoadingState rows={4} /></div> :
        q.error ? <ErrorState error={q.error} /> :
        !q.data?.length ? (
          <div className="card">
            <EmptyState
              message="No job postings yet"
              hint="Create a posting to open a role for applications."
              action={
                <button className="btn-primary" onClick={() => setShowCreate(true)}>+ New job posting</button>
              }
            />
          </div>
        ) : (
          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Title</th>
                    <th>Dept</th>
                    <th>Type</th>
                    <th className="text-right">Apps</th>
                    <th className="text-right">Hired</th>
                    <th>Status</th>
                    <th className="text-right w-20"></th>
                  </tr>
                </thead>
                <tbody>
                  {(q.data ?? []).map((j: any) => (
                    <tr key={j.id}>
                      <td className="font-mono text-xs font-semibold text-ink-900">{j.code}</td>
                      <td>
                        <div className="font-semibold text-ink-900">{j.title}</div>
                        {j.location && (
                          <div className="text-2xs text-ink-500 mt-0.5">
                            {j.location}{j.remote && ' · remote'}
                          </div>
                        )}
                      </td>
                      <td className="text-sm text-ink-700">{j.department_name ?? <span className="text-ink-400">—</span>}</td>
                      <td className="text-xs text-ink-600">{j.employment_type?.replace('_', ' ')}</td>
                      <td className="text-right font-mono font-semibold text-ink-900 tabular-nums">{j.application_count}</td>
                      <td className="text-right font-mono text-ink-700 tabular-nums">
                        <span className="font-semibold text-ink-900">{j.hired_count}</span>
                        <span className="text-ink-400">/{j.headcount}</span>
                      </td>
                      <td><JobStatusBadge status={j.status} /></td>
                      <td className="text-right">
                        {j.status === 'draft' && (
                          <button
                            className="text-xs font-semibold text-sage-700 hover:bg-sage-50 px-2 py-1 rounded transition-colors"
                            onClick={() => transitionMut.mutate({ id: j.id, status: 'open' })}
                          >
                            Publish
                          </button>
                        )}
                        {j.status === 'open' && (
                          <button
                            className="text-xs font-medium text-ink-500 hover:bg-canvas-subtle px-2 py-1 rounded transition-colors"
                            onClick={() => setClosingJob(j)}
                          >
                            Close
                          </button>
                        )}
                        <button
                          className="text-ink-400 hover:text-danger px-1.5 py-1 rounded transition-colors ml-1"
                          title="Delete job"
                          onClick={() => { if (confirm(`Delete job ${j.code}? This cannot be undone.`)) deleteJobMut.mutate(j.id); }}
                        >✕</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

      {showCreate && <CreateJobModal onClose={() => setShowCreate(false)}
        onCreated={() => { qc.invalidateQueries({ queryKey: ['recruit-jobs'] }); setShowCreate(false); }} />}

      {closingJob && (
        <CloseJobDialog
          job={closingJob}
          loading={transitionMut.isPending}
          onClose={() => setClosingJob(null)}
          onConfirm={() => transitionMut.mutate({ id: closingJob.id, status: 'closed' })}
        />
      )}
    </div>
  );
}

function CloseJobDialog({
  job, loading, onClose, onConfirm,
}: { job: any; loading: boolean; onClose: () => void; onConfirm: () => void }) {
  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow text-danger mb-1">People · Close</div>
        <h2 className="text-xl font-bold font-display text-ink-900">Close this job?</h2>
        <p className="text-sm text-ink-500 mt-2 leading-relaxed">
          Close <span className="font-mono font-semibold text-ink-900">{job.code}</span>
          {' '}— <span className="text-ink-700">{job.title}</span>?
          No new applications can be submitted. Existing candidates in the pipeline remain, and
          outstanding offers stay open.
        </p>
      </div>
      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Keep open</button>
        <button
          className="btn-primary !bg-danger hover:!bg-red-700"
          disabled={loading}
          onClick={onConfirm}
        >
          {loading ? 'Closing…' : 'Close job'}
        </button>
      </div>
    </Modal>
  );
}

function JobStatusBadge({ status }: { status: string }) {
  const cls = {
    draft:   'bg-canvas-subtle text-ink-700 ring-ink-200',
    open:    'bg-sage-50 text-sage-700 ring-sage-200',
    on_hold: 'bg-amber-50 text-amber-800 ring-amber-200',
    closed:  'bg-canvas-subtle text-ink-500 ring-ink-200',
  }[status] ?? 'bg-canvas-subtle text-ink-700 ring-ink-200';
  return (
    <span className={`inline-flex items-center font-semibold text-2xs uppercase tracking-wider px-2 py-0.5 rounded ring-1 ring-inset ${cls}`}>
      {status.replace('_', ' ')}
    </span>
  );
}

function KpiTile({
  label, value, accent = 'default',
}: {
  label: string;
  value: number | string;
  accent?: 'default' | 'sage' | 'amber' | 'danger';
}) {
  const bar = {
    default: 'bg-ink-200',
    sage: 'bg-sage-500',
    amber: 'bg-amber-500',
    danger: 'bg-danger',
  }[accent];
  return (
    <div className="card p-5 relative overflow-hidden">
      <div className={`absolute top-0 left-0 right-0 h-0.5 ${bar}`} />
      <div className="eyebrow text-ink-500">{label}</div>
      <div className="mt-3 text-3xl font-bold font-display text-ink-900 tabular-nums">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
    </div>
  );
}

function PipelineTab() {
  const qc = useQueryClient();
  const [jobFilter, setJobFilter] = useState('');
  const [showAddCandidate, setShowAddCandidate] = useState(false);
  const [selectedApp, setSelectedApp] = useState<string | null>(null);

  const jobsQ = useQuery({ queryKey: ['recruit-jobs-open'], queryFn: () => hrmApi.listJobs({ status: 'open' }) });
  const appsQ = useQuery({
    queryKey: ['recruit-apps', jobFilter],
    queryFn: () => hrmApi.listApplications({ jobPostingId: jobFilter || undefined, limit: 500 }),
  });

  const byStage: Record<string, any[]> = {};
  for (const s of STAGES) byStage[s.key] = [];
  for (const a of (appsQ.data ?? [])) (byStage[a.stage] ?? byStage.applied).push(a);

  return (
    <div>
      <div className="card p-3 mb-3 flex gap-3 items-end">
        <div>
          <label className="label">Filter by job</label>
          <select className="input w-64" value={jobFilter} onChange={(e) => setJobFilter(e.target.value)}>
            <option value="">All open jobs</option>
            {jobsQ.data?.map((j: any) => <option key={j.id} value={j.id}>{j.code} — {j.title}</option>)}
          </select>
        </div>
        <button className="btn-primary ml-auto" onClick={() => setShowAddCandidate(true)}>+ Add candidate</button>
      </div>

      {appsQ.isLoading ? <LoadingState /> : appsQ.error ? <ErrorState error={appsQ.error} /> : (
        <div className="grid grid-cols-6 gap-3 overflow-x-auto">
          {STAGES.map((stage) => {
            const apps = byStage[stage.key];
            return (
              <div key={stage.key} className="min-w-[180px]">
                <div className={`card p-2 mb-2 ${stage.color}`}>
                  <div className="flex justify-between items-center">
                    <div className="font-semibold text-xs">{stage.label}</div>
                    <div className="text-2xs">{apps.length}</div>
                  </div>
                </div>
                <div className="space-y-2">
                  {apps.length === 0 ? (
                    <div className="text-2xs text-ink-400 text-center py-4">—</div>
                  ) : apps.map((a: any) => (
                    <div key={a.id}
                      className={`card p-2 cursor-pointer hover:bg-canvas-subtle transition-colors ${selectedApp === a.id ? 'ring-2 ring-sage-400 bg-canvas-subtle' : ''}`}
                      onClick={() => setSelectedApp(a.id)}>
                      <div className="text-xs font-semibold text-ink-900 truncate">{a.candidate_name}</div>
                      <div className="text-2xs text-ink-500 truncate mt-0.5">
                        {a.candidate_title ?? <span className="text-ink-400">—</span>}
                        {a.candidate_years && (
                          <>
                            <span className="mx-1 text-ink-300">·</span>
                            <span className="font-mono tabular-nums">{a.candidate_years}y</span>
                          </>
                        )}
                      </div>
                      <div className="text-2xs text-ink-600 font-mono truncate mt-1">{a.job_code}</div>
                      {a.rating && (
                        <div className="text-2xs text-amber-600 mt-1 tracking-wider">{'★'.repeat(a.rating)}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {selectedApp && <ApplicationDetailModal applicationId={selectedApp}
        onClose={() => setSelectedApp(null)}
        onChanged={() => qc.invalidateQueries({ queryKey: ['recruit-apps'] })} />}
      {showAddCandidate && <AddCandidateModal onClose={() => setShowAddCandidate(false)}
        onCreated={() => { qc.invalidateQueries({ queryKey: ['recruit-apps'] }); setShowAddCandidate(false); }} />}
    </div>
  );
}

function ApplicationDetailModal({ applicationId, onClose, onChanged }:
    { applicationId: string; onClose: () => void; onChanged: () => void }) {
  const qc = useQueryClient();
  const [showOffer, setShowOffer] = useState(false);
  const [showReject, setShowReject] = useState(false);

  // No direct getApplication endpoint — pull from list
  const q = useQuery({
    queryKey: ['recruit-apps-single', applicationId],
    queryFn: () => hrmApi.listApplications({ limit: 500 }).then((all: any[]) =>
      all.find((a: any) => a.id === applicationId)),
  });

  const advanceMut = useMutation({
    mutationFn: (payload: any) => hrmApi.advanceApplicationStage(applicationId, payload),
    onSuccess: (d) => {
      toast.success(`Moved to ${d.toStage}`);
      q.refetch();
      onChanged();
      setShowReject(false);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (q.isLoading) return null;
  if (q.error) return <ErrorState error={q.error} />;
  const app = q.data;
  if (!app) return null;

  const currentIdx = STAGES.findIndex((s) => s.key === app.stage);
  const canAdvance = !['hired', 'rejected', 'withdrawn'].includes(app.stage);
  const nextStage = currentIdx >= 0 && currentIdx < 4 ? STAGES[currentIdx + 1] : null;

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden">
      <div className="px-6 pt-6 pb-2 flex-shrink-0">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="eyebrow mb-1">People · Application</div>
            <h2 className="text-xl font-bold font-display text-ink-900 truncate">{app.candidate_name}</h2>
            <div className="text-sm text-ink-500 mt-0.5 truncate">{app.candidate_email}</div>
            <div className="text-xs text-ink-500 mt-1">
              Applied for <span className="font-semibold text-ink-900">{app.job_title}</span>
              <span className="ml-1.5 font-mono text-ink-700">{app.job_code}</span>
            </div>
          </div>
          <button
            className="w-8 h-8 flex items-center justify-center text-ink-400 hover:text-ink-900 hover:bg-canvas-subtle rounded-md transition-colors flex-shrink-0"
            onClick={onClose}
            aria-label="Close"
          >✕</button>
        </div>
      </div>

      <div className="px-6 py-4 overflow-y-auto flex-1 min-h-0">
        {/* Candidate facts */}
        <div className="bg-canvas-subtle rounded-xl p-4 mb-4 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          {app.candidate_company && (
            <div>
              <div className="eyebrow text-ink-500 text-2xs mb-0.5">Current</div>
              <div className="text-ink-900">{app.candidate_title} <span className="text-ink-500">@ {app.candidate_company}</span></div>
            </div>
          )}
          {app.candidate_years && (
            <div>
              <div className="eyebrow text-ink-500 text-2xs mb-0.5">Experience</div>
              <div className="text-ink-900 font-mono tabular-nums">{app.candidate_years} years</div>
            </div>
          )}
          <div>
            <div className="eyebrow text-ink-500 text-2xs mb-0.5">Applied</div>
            <div className="text-ink-900 font-mono tabular-nums">
              {new Date(app.applied_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
            </div>
          </div>
          {app.rating && (
            <div>
              <div className="eyebrow text-ink-500 text-2xs mb-0.5">Rating</div>
              <div className="text-amber-600 tracking-wider">{'★'.repeat(app.rating)}</div>
            </div>
          )}
        </div>

        {/* Stage progress */}
        <div className="eyebrow text-ink-500 mb-2">Stage</div>
        <div className="flex items-center gap-1 mb-4 flex-wrap">
          {STAGES.map((s, i) => {
            const isActive = s.key === app.stage;
            return (
              <span key={s.key} className="flex items-center">
                <span
                  className={`inline-flex items-center font-semibold text-2xs uppercase tracking-wider px-2 py-0.5 rounded ring-1 ring-inset ${
                    isActive
                      ? s.color
                      : 'bg-canvas-elevated text-ink-400 ring-ink-100'
                  }`}
                >
                  {s.label}
                </span>
                {i < STAGES.length - 1 && i < 4 && (
                  <span className="mx-1 text-ink-300">→</span>
                )}
              </span>
            );
          })}
        </div>

        {/* Alerts */}
        {app.rejection_reason && (
          <div className="bg-red-50 border border-red-200 rounded-xl px-3 py-2 text-xs text-red-700 mb-3">
            <div className="font-semibold">Rejected</div>
            <div className="mt-0.5">{app.rejection_reason}</div>
          </div>
        )}
        {app.notes && (
          <div className="bg-canvas-subtle rounded-xl px-3 py-2.5 text-xs text-ink-700 mb-3 whitespace-pre-line leading-relaxed">
            {app.notes}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 flex-wrap justify-end flex-shrink-0">
        {canAdvance && (
          <button className="btn-secondary text-xs"
            onClick={() => setShowReject(true)}>
            Reject
          </button>
        )}
        {canAdvance && nextStage && (
          <button className="btn-secondary text-xs" disabled={advanceMut.isPending}
            onClick={() => advanceMut.mutate({ toStage: nextStage.key })}>
            {advanceMut.isPending ? 'Moving…' : `Advance → ${nextStage.label}`}
          </button>
        )}
        {app.stage === 'offer' && (
          <button className="btn-primary text-xs" onClick={() => setShowOffer(true)}>
            Create offer
          </button>
        )}
        {['interview', 'offer'].includes(app.stage) && (
          <button className="btn-primary text-xs !bg-sage-700 hover:!bg-sage-800"
            disabled={advanceMut.isPending}
            onClick={() => advanceMut.mutate({ toStage: 'hired' })}>
            {advanceMut.isPending ? 'Hiring…' : 'Mark hired'}
          </button>
        )}
      </div>

      {showOffer && <CreateOfferModal applicationId={applicationId} jobTitle={app.job_title}
        onClose={() => setShowOffer(false)}
        onCreated={() => { setShowOffer(false); qc.invalidateQueries({ queryKey: ['recruit-offers'] }); onChanged(); }} />}

      {showReject && (
        <RejectApplicationDialog
          candidateName={app.candidate_name}
          loading={advanceMut.isPending}
          onClose={() => setShowReject(false)}
          onConfirm={(reason) => advanceMut.mutate({ toStage: 'rejected', rejectionReason: reason })}
        />
      )}
    </Modal>
  );
}

function RejectApplicationDialog({
  candidateName, loading, onClose, onConfirm,
}: { candidateName: string; loading: boolean; onClose: () => void; onConfirm: (reason: string) => void }) {
  const [reason, setReason] = useState('');
  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-[60] p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow text-danger mb-1">People · Reject</div>
        <h2 className="text-xl font-bold font-display text-ink-900">Reject application?</h2>
        <p className="text-sm text-ink-500 mt-2 leading-relaxed">
          Reject <span className="font-semibold text-ink-900">{candidateName}</span>'s application?
          The reason is logged for audit and can be referenced if they reapply later.
        </p>
      </div>
      <div className="px-6 py-5">
        <label className="label">Reason <span className="text-danger">*</span></label>
        <textarea className="input" rows={3} value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Strong candidate but experience doesn't match this role's seniority" />
      </div>
      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button
          className="btn-primary !bg-danger hover:!bg-red-700"
          disabled={!reason.trim() || loading}
          onClick={() => onConfirm(reason.trim())}
        >
          {loading ? 'Rejecting…' : 'Reject application'}
        </button>
      </div>
    </Modal>
  );
}

function CreateJobModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [code, setCode] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const [remote, setRemote] = useState(false);
  const [employmentType, setEmploymentType] = useState<'full_time'|'part_time'|'contract'|'intern'>('full_time');
  const [salaryMin, setSalaryMin] = useState<string>('');
  const [salaryMax, setSalaryMax] = useState<string>('');
  const [headcount, setHeadcount] = useState(1);
  const [publishNow, setPublishNow] = useState(true);

  const mut = useMutation({
    mutationFn: () => hrmApi.createJob({
      code: code.trim() ? code.toUpperCase() : undefined, title,
      description: description || undefined,
      location: location || undefined,
      remote, employmentType,
      salaryMin: salaryMin ? parseFloat(salaryMin) : undefined,
      salaryMax: salaryMax ? parseFloat(salaryMax) : undefined,
      headcount, publishNow,
    }),
    onSuccess: () => { toast.success(publishNow ? 'Job posted' : 'Draft saved'); onCreated(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-xl w-full max-h-[90vh] flex flex-col overflow-hidden">
      <div className="px-6 pt-6 pb-2 flex-shrink-0">
        <div className="eyebrow mb-1">People · New</div>
        <h2 className="text-xl font-bold font-display text-ink-900">New job posting</h2>
        <p className="text-sm text-ink-500 mt-1">
          Publish now to start accepting candidates, or save as draft to iterate on the JD first.
        </p>
      </div>

      <div className="px-6 py-5 grid grid-cols-2 gap-3 overflow-y-auto flex-1 min-h-0">
        <div>
          <label className="label">Code <span className="text-ink-400 font-normal">(auto if blank)</span></label>
          <input className="input font-mono uppercase" value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Auto (JOB-0001)" />
        </div>
        <div>
          <label className="label">Type</label>
          <select className="input" value={employmentType} onChange={(e) => setEmploymentType(e.target.value as any)}>
            <option value="full_time">Full time</option>
            <option value="part_time">Part time</option>
            <option value="contract">Contract</option>
            <option value="intern">Intern</option>
          </select>
        </div>
        <div className="col-span-2">
          <label className="label">Title <span className="text-danger">*</span></label>
          <input className="input" value={title} onChange={(e) => setTitle(e.target.value)}
            placeholder="Senior engineer" />
        </div>
        <div className="col-span-2">
          <label className="label">Description <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <textarea className="input" rows={3} value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Role summary, responsibilities, requirements" />
        </div>
        <div>
          <label className="label">Location <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <input className="input" value={location} onChange={(e) => setLocation(e.target.value)}
            placeholder="San Francisco" />
        </div>
        <div className="flex items-end pb-2">
          <label className="flex items-center gap-2 text-sm text-ink-700 cursor-pointer">
            <input type="checkbox" checked={remote} onChange={(e) => setRemote(e.target.checked)} />
            Remote-friendly
          </label>
        </div>
        <div>
          <label className="label">Salary min <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <input type="number" min={0} className="input tabular-nums" value={salaryMin}
            onChange={(e) => setSalaryMin(e.target.value)} />
        </div>
        <div>
          <label className="label">Salary max <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <input type="number" min={0} className="input tabular-nums" value={salaryMax}
            onChange={(e) => setSalaryMax(e.target.value)} />
        </div>
        <div>
          <label className="label">Headcount</label>
          <input type="number" min={1} className="input tabular-nums" value={headcount}
            onChange={(e) => setHeadcount(parseInt(e.target.value) || 1)} />
        </div>
        <div className="flex items-end pb-2">
          <label className="flex items-center gap-2 text-sm text-ink-700 cursor-pointer">
            <input type="checkbox" checked={publishNow} onChange={(e) => setPublishNow(e.target.checked)} />
            Publish immediately
          </label>
        </div>
      </div>

      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end flex-shrink-0">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={!title || mut.isPending}
          onClick={() => mut.mutate()}>
          {mut.isPending ? 'Creating…' : (publishNow ? 'Create + publish' : 'Save draft')}
        </button>
      </div>
    </Modal>
  );
}

function AddCandidateModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const jobsQ = useQuery({ queryKey: ['recruit-jobs-open'], queryFn: () => hrmApi.listJobs({ status: 'open' }) });

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [currentCompany, setCurrentCompany] = useState('');
  const [currentTitle, setCurrentTitle] = useState('');
  const [yearsExperience, setYearsExperience] = useState<string>('');
  const [source, setSource] = useState('');
  const [jobPostingId, setJobPostingId] = useState('');
  const [notes, setNotes] = useState('');

  const mut = useMutation({
    mutationFn: () => hrmApi.createCandidate({
      fullName,
      email: email || undefined,
      phone: phone || undefined,
      currentCompany: currentCompany || undefined,
      currentTitle: currentTitle || undefined,
      yearsExperience: yearsExperience ? parseFloat(yearsExperience) : undefined,
      source: source || undefined,
      jobPostingId: jobPostingId || undefined,
      notes: notes || undefined,
    }),
    onSuccess: (d) => {
      toast.success(d.applicationId ? 'Candidate + application added' : 'Candidate added');
      onCreated();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-xl w-full max-h-[90vh] flex flex-col overflow-hidden">
      <div className="px-6 pt-6 pb-2 flex-shrink-0">
        <div className="eyebrow mb-1">People · Candidate</div>
        <h2 className="text-xl font-bold font-display text-ink-900">Add candidate</h2>
        <p className="text-sm text-ink-500 mt-1">
          Add a candidate to the pool, or apply them directly to an open job.
        </p>
      </div>

      <div className="px-6 py-5 grid grid-cols-2 gap-3 overflow-y-auto flex-1 min-h-0">
        <div className="col-span-2">
          <label className="label">Full name <span className="text-danger">*</span></label>
          <input className="input" value={fullName} onChange={(e) => setFullName(e.target.value)}
            placeholder="Jane Doe" />
        </div>
        <div>
          <label className="label">Email <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <input type="email" className="input" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div>
          <label className="label">Phone <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <input className="input" value={phone} onChange={(e) => setPhone(e.target.value)} />
        </div>
        <div>
          <label className="label">Current company <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <input className="input" value={currentCompany} onChange={(e) => setCurrentCompany(e.target.value)} />
        </div>
        <div>
          <label className="label">Current title <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <input className="input" value={currentTitle} onChange={(e) => setCurrentTitle(e.target.value)} />
        </div>
        <div>
          <label className="label">Years of experience</label>
          <input type="number" min={0} step={0.5} className="input tabular-nums" value={yearsExperience}
            onChange={(e) => setYearsExperience(e.target.value)} />
        </div>
        <div>
          <label className="label">Source</label>
          <input className="input" value={source} onChange={(e) => setSource(e.target.value)}
            placeholder="referral, linkedin, website" />
        </div>
        <div className="col-span-2">
          <label className="label">Apply to job <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <select className="input" value={jobPostingId} onChange={(e) => setJobPostingId(e.target.value)}>
            <option value="">Just add to candidate pool</option>
            {jobsQ.data?.map((j: any) => <option key={j.id} value={j.id}>{j.code} — {j.title}</option>)}
          </select>
        </div>
        <div className="col-span-2">
          <label className="label">Notes <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <textarea className="input" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
            placeholder="First impressions, referrer, links" />
        </div>
      </div>

      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end flex-shrink-0">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={!fullName || mut.isPending}
          onClick={() => mut.mutate()}>
          {mut.isPending ? 'Adding…' : 'Add candidate'}
        </button>
      </div>
    </Modal>
  );
}

function CreateOfferModal({ applicationId, jobTitle, onClose, onCreated }:
    { applicationId: string; jobTitle: string; onClose: () => void; onCreated: () => void }) {
  const [title, setTitle] = useState(jobTitle);
  const [baseSalary, setBaseSalary] = useState(0);
  const [signingBonus, setSigningBonus] = useState(0);
  const [startDate, setStartDate] = useState('');
  const [expiresOn, setExpiresOn] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 7); return d.toISOString().slice(0, 10);
  });
  const [benefitsSummary, setBenefitsSummary] = useState('');

  const mut = useMutation({
    mutationFn: () => hrmApi.createOffer({
      applicationId, title, baseSalary, signingBonus,
      startDate: startDate || undefined,
      expiresOn: expiresOn || undefined,
      benefitsSummary: benefitsSummary || undefined,
    }),
    onSuccess: (d) => { toast.success(`Offer ${d.offerNumber} created`); onCreated(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-[60] p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-lg w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow mb-1">People · Offer</div>
        <h2 className="text-xl font-bold font-display text-ink-900">Create offer</h2>
        <p className="text-sm text-ink-500 mt-1">
          Draft an offer. The candidate will receive it once you send from the offers tab.
        </p>
      </div>

      <div className="px-6 py-5 grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <label className="label">Position title <span className="text-danger">*</span></label>
          <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} />
        </div>
        <div>
          <label className="label">Base salary <span className="text-danger">*</span></label>
          <input type="number" min={0} step={1000}
            className="input tabular-nums" value={baseSalary}
            onChange={(e) => setBaseSalary(parseFloat(e.target.value) || 0)} />
        </div>
        <div>
          <label className="label">Signing bonus <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <input type="number" min={0} step={100}
            className="input tabular-nums" value={signingBonus}
            onChange={(e) => setSigningBonus(parseFloat(e.target.value) || 0)} />
        </div>
        <div>
          <label className="label">Start date <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <input type="date" className="input" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </div>
        <div>
          <label className="label">Expires</label>
          <input type="date" className="input" value={expiresOn} onChange={(e) => setExpiresOn(e.target.value)} />
        </div>
        <div className="col-span-2">
          <label className="label">Benefits summary <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <textarea className="input" rows={3} value={benefitsSummary}
            onChange={(e) => setBenefitsSummary(e.target.value)}
            placeholder="Health insurance, 401k match, 21 days PTO…" />
        </div>
      </div>

      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={!title || baseSalary <= 0 || mut.isPending}
          onClick={() => mut.mutate()}>
          {mut.isPending ? 'Creating…' : 'Create draft offer'}
        </button>
      </div>
    </Modal>
  );
}

function OffersTab() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['recruit-offers'], queryFn: () => hrmApi.listOffers() });

  const [decliningOffer, setDecliningOffer] = useState<any | null>(null);

  const sendMut = useMutation({
    mutationFn: (id: string) => hrmApi.sendOffer(id),
    onSuccess: () => { toast.success('Offer sent'); qc.invalidateQueries({ queryKey: ['recruit-offers'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const respondMut = useMutation({
    mutationFn: ({ id, accept, reason }: any) => hrmApi.respondOffer(id, accept, reason),
    onSuccess: (d) => {
      toast.success(`Marked ${d.status}`);
      qc.invalidateQueries({ queryKey: ['recruit-offers'] });
      setDecliningOffer(null);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const [onboardCandidateId, setOnboardCandidateId] = useState<string | null>(null);

  if (q.isLoading) return <div className="card"><LoadingState rows={4} /></div>;
  if (q.error) return <ErrorState error={q.error} />;

  return (
    <div>
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>Offer #</th>
                <th>Candidate</th>
                <th>Position</th>
                <th className="text-right">Base</th>
                <th>Status</th>
                <th className="text-right w-48"></th>
              </tr>
            </thead>
            <tbody>
              {!q.data?.length ? (
                <tr><td colSpan={6}><EmptyState message="No offers yet." /></td></tr>
              ) : (q.data ?? []).map((o: any) => (
                <tr key={o.id}>
                  <td className="font-mono text-xs font-semibold text-ink-700">{o.offer_number}</td>
                  <td>
                    <div className="text-sm text-ink-900">{o.candidate_name}</div>
                    <div className="text-2xs text-ink-500 mt-0.5">{o.candidate_email}</div>
                  </td>
                  <td className="text-sm text-ink-900">{o.title}</td>
                  <td className="text-right font-mono font-semibold tabular-nums text-sm text-ink-900">
                    ${parseFloat(o.base_salary).toFixed(0)}
                  </td>
                  <td><OfferStatusBadge status={o.status} /></td>
                  <td className="text-right">
                    <div className="flex gap-1 justify-end items-center">
                      {o.status === 'draft' && (
                        <button
                          className="text-xs font-semibold text-sage-700 hover:bg-sage-50 px-2 py-1 rounded transition-colors"
                          disabled={sendMut.isPending}
                          onClick={() => sendMut.mutate(o.id)}
                        >
                          {sendMut.isPending ? 'Sending…' : 'Send'}
                        </button>
                      )}
                      {o.status === 'sent' && (
                        <>
                          <button
                            className="text-xs font-semibold text-sage-700 hover:bg-sage-50 px-2 py-1 rounded transition-colors"
                            disabled={respondMut.isPending}
                            onClick={() => respondMut.mutate({ id: o.id, accept: true })}
                          >
                            Accept
                          </button>
                          <button
                            className="text-xs font-semibold text-danger hover:bg-red-50 px-2 py-1 rounded transition-colors"
                            onClick={() => setDecliningOffer(o)}
                          >
                            Decline
                          </button>
                        </>
                      )}
                      {o.status === 'accepted' && (
                        <button
                          className="text-xs font-semibold text-sage-700 hover:bg-sage-50 px-2 py-1 rounded transition-colors"
                          onClick={async () => {
                            const app = await hrmApi.listApplications({ limit: 500 })
                              .then((as: any[]) => as.find((a) => a.id === o.application_id));
                            if (app) setOnboardCandidateId(app.candidate_id);
                          }}
                        >
                          Onboard →
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {onboardCandidateId && <OnboardModal candidateId={onboardCandidateId}
        onClose={() => setOnboardCandidateId(null)}
        onOnboarded={() => { setOnboardCandidateId(null); qc.invalidateQueries({ queryKey: ['recruit-offers'] }); }} />}

      {decliningOffer && (
        <DeclineOfferDialog
          offer={decliningOffer}
          loading={respondMut.isPending}
          onClose={() => setDecliningOffer(null)}
          onConfirm={(reason) => respondMut.mutate({ id: decliningOffer.id, accept: false, reason })}
        />
      )}
    </div>
  );
}

function DeclineOfferDialog({
  offer, loading, onClose, onConfirm,
}: { offer: any; loading: boolean; onClose: () => void; onConfirm: (reason: string | undefined) => void }) {
  const [reason, setReason] = useState('');
  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow text-danger mb-1">People · Decline</div>
        <h2 className="text-xl font-bold font-display text-ink-900">Mark offer declined?</h2>
        <p className="text-sm text-ink-500 mt-2 leading-relaxed">
          Record <span className="font-mono font-semibold text-ink-900">{offer.offer_number}</span>
          {' '}(<span className="text-ink-700">{offer.candidate_name}</span>) as declined?
          Helpful to capture why so trends can inform future offers.
        </p>
      </div>
      <div className="px-6 py-5">
        <label className="label">Reason <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
        <textarea className="input" rows={3} value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Accepted another offer, compensation didn't meet expectation" />
      </div>
      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button
          className="btn-primary !bg-danger hover:!bg-red-700"
          disabled={loading}
          onClick={() => onConfirm(reason.trim() || undefined)}
        >
          {loading ? 'Saving…' : 'Mark declined'}
        </button>
      </div>
    </Modal>
  );
}

function OfferStatusBadge({ status }: { status: string }) {
  const cls = {
    draft:     'bg-canvas-subtle text-ink-700 ring-ink-200',
    sent:      'bg-blue-50 text-blue-700 ring-blue-200',
    accepted:  'bg-sage-50 text-sage-700 ring-sage-200',
    declined:  'bg-red-50 text-red-700 ring-red-200',
    expired:   'bg-amber-50 text-amber-800 ring-amber-200',
    withdrawn: 'bg-canvas-subtle text-ink-500 ring-ink-200',
  }[status] ?? 'bg-canvas-subtle text-ink-700 ring-ink-200';
  return (
    <span className={`inline-flex items-center font-semibold text-2xs uppercase tracking-wider px-2 py-0.5 rounded ring-1 ring-inset ${cls}`}>
      {status}
    </span>
  );
}

function OnboardModal({ candidateId, onClose, onOnboarded }:
    { candidateId: string; onClose: () => void; onOnboarded: () => void }) {
  const candQ = useQuery({
    queryKey: ['candidate', candidateId],
    queryFn: () => hrmApi.getCandidate(candidateId),
  });

  const today = new Date().toISOString().slice(0, 10);
  const [employeeCode, setEmployeeCode] = useState('');
  const [hireDate, setHireDate] = useState(today);
  const [designation, setDesignation] = useState('');
  const [baseSalary, setBaseSalary] = useState(0);
  const [contractType, setContractType] = useState<'full_time'|'part_time'|'contract'|'intern'>('full_time');
  const [prefilled, setPrefilled] = useState(false);

  const cand = candQ.data;
  useEffect(() => {
    if (!cand || prefilled) return;
    const acceptedOffer = cand.offers?.find((o: any) => o.status === 'accepted');
    if (acceptedOffer) {
      setDesignation(acceptedOffer.title);
      setBaseSalary(parseFloat(acceptedOffer.base_salary));
    }
    setEmployeeCode('EMP-' + cand.full_name.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 6));
    setPrefilled(true);
  }, [cand, prefilled]);

  const mut = useMutation({
    mutationFn: () => hrmApi.onboardCandidate(candidateId, {
      employeeCode, hireDate,
      designation: designation || undefined,
      baseSalary,
      contractType,
    }),
    onSuccess: (d) => { toast.success(`Onboarded as ${d.employeeCode}`); onOnboarded(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-[60] p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-lg w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow mb-1">People · Onboard</div>
        <h2 className="text-xl font-bold font-display text-ink-900">
          Onboard {cand?.full_name ?? 'candidate'}
        </h2>
        <p className="text-sm text-ink-500 mt-1">
          Creates an employee record linked to this candidate. The accepted offer pre-fills title and salary.
        </p>
      </div>

      <div className="px-6 py-5 grid grid-cols-2 gap-3">
        <div>
          <label className="label">Employee code <span className="text-danger">*</span></label>
          <input className="input font-mono uppercase" value={employeeCode}
            onChange={(e) => setEmployeeCode(e.target.value.toUpperCase())} />
        </div>
        <div>
          <label className="label">Hire date <span className="text-danger">*</span></label>
          <input type="date" className="input" value={hireDate} onChange={(e) => setHireDate(e.target.value)} />
        </div>
        <div className="col-span-2">
          <label className="label">Designation</label>
          <input className="input" value={designation} onChange={(e) => setDesignation(e.target.value)} />
        </div>
        <div>
          <label className="label">Base salary</label>
          <input type="number" min={0} step={100}
            className="input tabular-nums" value={baseSalary}
            onChange={(e) => setBaseSalary(parseFloat(e.target.value) || 0)} />
        </div>
        <div>
          <label className="label">Contract type</label>
          <select className="input" value={contractType} onChange={(e) => setContractType(e.target.value as any)}>
            <option value="full_time">Full time</option>
            <option value="part_time">Part time</option>
            <option value="contract">Contract</option>
            <option value="intern">Intern</option>
          </select>
        </div>
      </div>

      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={!employeeCode || !hireDate || mut.isPending}
          onClick={() => mut.mutate()}>
          {mut.isPending ? 'Onboarding…' : 'Onboard'}
        </button>
      </div>
    </Modal>
  );
}
